import { Injectable } from '@angular/core';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { ExternalToast, toast, ToastOptions } from 'ngx-sonner';
import { ToastContentComponent, ToastListContent } from '@shared/components/toast-content/toast-content.component';

// applied to every toast via <ngx-sonner-toaster [toastOptions]="TOAST_CLASS_OPTIONS">
// in app.component.html
export const TOAST_CLASS_OPTIONS: ToastOptions = {
  classes: {
    error: 'toast-error',
    warning: 'toast-warning',
    info: 'toast-info',
    success: 'toast-success',
    title: 'toast-title',
    description: 'toast-message',
    closeButton: 'toast-close-button'
  }
};

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private pendingAnnouncements: string[] = [];
  
  // ngx sonner has a bug that doesn't handle named toasts that use
  // duration infinity ... as a work-around, manually track named
  // toasts to avoid showing the same toast message twice
  private activeCustomToasts = new Set<string | number>();
  
  constructor(private liveAnnouncer: LiveAnnouncer) {}
  
  public showErrorToast(message: string, title: string = 'Error', id: string = null) {
    if (id != null) {
      // manual tracking only works when ngx sonner object has been initialized

      const selectorResult = document.querySelector('ngx-sonner-toaster');

      if (selectorResult == null)
        return;

      if (this.activeCustomToasts.has(id))
        return;

      this.activeCustomToasts.add(id);
    }

    toast.error(title, {
      description: message,
      position: 'top-right',
      duration: Infinity,
      closeButton: true,
      dismissible: true,
      id: id,
      // When the user explicitly clicks the close button or wipes the toast, 
      // remove it from our active tracker so it can be called again in the future.
      onDismiss: () => this.activeCustomToasts.delete(id),
      onAutoClose: () => this.activeCustomToasts.delete(id)
    });
    this.announce(title, message);
  }

  public showSuccessToast(title: string, message: string) {
    toast.success(title, {
      description: message,
      position: 'top-center',
      duration: 5000,
      closeButton: true,
      dismissible: true
    });
  }

  public showInfoToast(title: string, message: string, duration: number = 5000) {
    toast.info(title, {
      description: message,
      position: 'top-right',
      duration: duration == 0 ? Infinity : duration,
      closeButton: true,
      dismissible: true
    });
    this.announce(title, message);
  }

  public showWarningToast(title: string, message: string) {
    toast.warning(title, {
      description: message,
      position: 'top-right',
      duration: Infinity,
      closeButton: true,
      dismissible: true
    });
    this.announce(title, message);
  }

  // for rich/list-style content (multiple labeled items)
  // title renders normally (plain text); the body is our component, passed via the
  // `description` slot, which ngx-sonner also allows to be a Type<unknown>.
  public showErrorListToast(title: string, content: ToastListContent) {
    this.showListToast(toast.error, title, content);
  }

  public showWarningListToast(title: string, content: ToastListContent) {
    this.showListToast(toast.warning, title, content);
  }

  private showListToast(
    toastFn: (message: string, data?: ExternalToast) => string | number,
    title: string,
    content: ToastListContent
  ) {
    toastFn(title, {
      description: ToastContentComponent,
      componentProps: { content },
      position: 'top-right',
      duration: Infinity,
      closeButton: true,
      dismissible: true
    });
    this.announce(title, [content.header, content.intro, ...(content.items ?? [])].filter(Boolean).join('. '));
  }

  public closeAllToasts(): void {
    toast.dismiss();
  }

  // a status message with no toast (e.g. a loader's "loading"), read in order with the toasts. One that's already
  // waiting isn't added again, so several loaders appearing together are read once
  public announceStatus(message: string): void {
    if (!this.pendingAnnouncements.includes(message)) {
      this.queueAnnouncement(message);
    }
  }

  private announce(title: string, message: string): void {
    this.queueAnnouncement(`${title}. ${message}`);
  }

  // ngx-sonner creates each toast's live region together with its text, which screen readers skip,
  // so read the toast through the CDK's live region, which is already on the page (as Material's snackbars do).
  // toasts that fire together (e.g. several server errors) would replace each other there, so each call announces
  // everything still waiting, in order; the list empties once the CDK has put it in the live region
  private queueAnnouncement(text: string): void {
    this.pendingAnnouncements.push(text);
    this.liveAnnouncer.announce(this.pendingAnnouncements.join(' ')).then(() => {
      this.pendingAnnouncements = [];
    });
  }
}
